import { compact } from 'lodash';
import api from 'core/util/api';
import { queryClient } from 'app/queryClient';
import createLookup, {
  hasId,
  LookupId,
  LookupManyResult,
  LookupResult,
  lookupQueryKey,
  uniqueIds
} from 'app/util/lookups/createLookup';
import { DEVICE_INDEX_MAX_RESULTS } from '@kentik/ui-shared/device/constants';
import type { DeviceIndexEntry } from '@kentik/ui-shared/types/deviceIndex.types';

export type { DeviceIndexEntry };

/*
 * Two caches:
 * - deviceRecords: device by id. The source of truth.
 * - deviceNameIndex: device name -> id.
 *
 * Both fetch device index records (DeviceIndexEntry) from the same endpoint:
 * - Fetching by id stores each device in deviceRecords and maps its name in deviceNameIndex.
 * - Fetching by name stores each device in deviceRecords too; deviceNameIndex keeps only name -> id.
 *
 * Name lookups find the id, then read the device by id, and skip it if the device has since been renamed.
 */

const CACHE_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const NAME_INDEX = 'deviceIndexByName';

const fetchDeviceIndex = (identifiers: { ids: string[] } | { names: string[] }): Promise<DeviceIndexEntry[]> =>
  api
    .post('/api/ui/lookups/devices/find', { data: identifiers, showErrorToast: false })
    .then((devices: DeviceIndexEntry[] | null) => devices || []);

// overwrites, so a name cached as not found picks up a device that takes it later
const seedNames = (devices: DeviceIndexEntry[]) =>
  devices.forEach(({ id, device_name }) => {
    if (device_name) {
      queryClient.setQueryData(lookupQueryKey(NAME_INDEX, device_name), { id: String(id), device_name });
    }
  });

const deviceRecords = createLookup<DeviceIndexEntry>('deviceIndex', (ids) => fetchDeviceIndex({ ids }), {
  onReceive: seedNames,
  batchSize: DEVICE_INDEX_MAX_RESULTS,
  staleTime: CACHE_STALE_TIME
});

const deviceNameIndex = createLookup<{ id: string; device_name: string }>(
  NAME_INDEX,
  (names) =>
    fetchDeviceIndex({ names }).then((found) => {
      deviceRecords.ingest(found);
      return found.map(({ id, device_name }) => ({ id: String(id), device_name }));
    }),
  {
    getKey: (mapping) => mapping.device_name,
    batchSize: DEVICE_INDEX_MAX_RESULTS,
    staleTime: CACHE_STALE_TIME
  }
);

// null if the device was renamed away from this name
const matches = (device: DeviceIndexEntry | null | undefined, name: string) =>
  device && device.device_name !== name ? null : device;

const useLookupManyByName = (names: LookupId[]): LookupManyResult<DeviceIndexEntry> => {
  const mappings = deviceNameIndex.useLookupMany(names);
  const devices = deviceRecords.useLookupMany(mappings.data?.map(({ id }) => id) ?? []);
  const requested = new Set(mappings.data?.map(({ device_name }) => device_name));

  return {
    data: mappings.data && devices.data?.filter(({ device_name }) => requested.has(device_name)),
    isLoading: mappings.isLoading || devices.isLoading,
    error: mappings.error ?? devices.error
  };
};

const useLookupByName = (name: LookupId): LookupResult<DeviceIndexEntry> => {
  const { data, ...result } = useLookupManyByName([name]);

  if (!hasId(name) || !data) {
    return { ...result, data: undefined };
  }

  return { ...result, data: data[0] ?? null };
};

const lookupByName = (name: LookupId): Promise<DeviceIndexEntry | null> =>
  deviceNameIndex.lookup(name).then((mapping) => {
    if (!mapping) {
      return null;
    }

    return deviceRecords.lookup(mapping.id).then((device) => matches(device, String(name)) ?? null);
  });

const lookupManyByName = (names: LookupId[]): Promise<DeviceIndexEntry[]> =>
  Promise.all(uniqueIds(names).map(lookupByName)).then(compact);

const caches = [deviceRecords, deviceNameIndex];

export const deviceIndex = {
  ...deviceRecords,
  clear: () => Promise.all(caches.map((cache) => cache.clear())),
  remove: () => caches.forEach((cache) => cache.remove()),
  lookupByName,
  lookupManyByName,
  useLookupByName,
  useLookupManyByName
};

export const useDeviceLookup = (deviceId: LookupId) => deviceIndex.useLookup(deviceId);

export const useDeviceLookupMany = (deviceIds: LookupId[]) => deviceIndex.useLookupMany(deviceIds);

export const useDeviceLookupByName = (deviceName: LookupId) => deviceIndex.useLookupByName(deviceName);

export const useDeviceLookupManyByName = (deviceNames: LookupId[]) => deviceIndex.useLookupManyByName(deviceNames);
